import React from 'react';

import { Card, Collapse, Empty, Flex } from 'antd-v5';
import { MdAddCircle } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { Button } from 'scouting-ui/lib';
import { Text } from 'scouting-ui/lib/Text';
import { cssVars } from 'scouting-ui/lib/variables';

import AdvancementRow from './AdvancementRow';
import styles from './YouthAdvancementsCard.module.scss';
import { groupAdvancementsByStatus, recentlyUpdated } from './groups';
import type { YouthAdvancementItem } from './types';

/** The status filters the tiles stand for, in the order they are shown. */
export type AdvancementTileStatus =
  | 'started'
  | 'completed'
  | 'approved'
  | 'notStarted';

export interface YouthAdvancementsCardProps {
  /** Section heading, e.g. `<FormattedMessage id="shared.AdvancementType.meritBadges.plural" />`. */
  title: React.ReactNode;
  /**
   * Every advancement of this type for the youth. The card does no fetching:
   * the caller maps its API response onto `YouthAdvancementItem`.
   */
  items?: YouthAdvancementItem[];
  loading?: boolean;
  /** Rendered beside the title, e.g. a "Print Blue Cards" button. */
  headerActions?: React.ReactNode;
  defaultOpen?: boolean;
  /** Makes the count tiles clickable and adds a "Start New" tile. */
  onViewStatus?: (status: AdvancementTileStatus) => void;
  /** Adds a "View More" button to each row, handed the whole item. */
  onViewMore?: (item: YouthAdvancementItem) => void;
  /** Adds the "View All" link below the list. */
  onViewAll?: () => void;
}

interface StatTileProps {
  status: AdvancementTileStatus;
  count: number;
  onClick?: (status: AdvancementTileStatus) => void;
}

function StatTile(props: StatTileProps) {
  const { status, count, onClick } = props;

  const contents = (
    <React.Fragment>
      <Text strong className={styles.tileValue}>
        {count}
      </Text>
      <Text
        strong
        font="body-body-3"
        color="color-info"
        className={styles.tileLabel}
      >
        <FormattedMessage id={`youthAdvancementsCard.${status}`} />
      </Text>
    </React.Fragment>
  );

  // A tile with nowhere to go is a figure, not a control: no button semantics
  if (!onClick) return <div className={styles.tile}>{contents}</div>;

  return (
    <button
      type="button"
      className={styles.tile}
      onClick={() => onClick(status)}
    >
      {contents}
    </button>
  );
}

export default function YouthAdvancementsCard(
  props: YouthAdvancementsCardProps,
) {
  const {
    title,
    items,
    loading,
    headerActions,
    defaultOpen = true,
    onViewStatus,
    onViewMore,
    onViewAll,
  } = props;

  const groups = groupAdvancementsByStatus(items);
  const recent = recentlyUpdated(items);

  const header = (
    <div className={styles.header}>
      <Text
        strong
        font="body-body-2"
        color="color-primary-oldScoutingBlue"
        className={styles.title}
      >
        {title}
      </Text>
      {headerActions && (
        // The header is the collapse trigger, so actions keep clicks to themselves
        <span
          className={styles.headerActions}
          onClick={event => event.stopPropagation()}
        >
          {headerActions}
        </span>
      )}
    </div>
  );

  const body = (
    <React.Fragment>
      <Flex wrap gap="middle" className={styles.tiles}>
        <StatTile
          status="started"
          count={groups.started.length}
          onClick={onViewStatus}
        />
        <StatTile
          status="completed"
          count={groups.completed.length}
          onClick={onViewStatus}
        />
        <StatTile
          status="approved"
          count={groups.approved.length}
          onClick={onViewStatus}
        />
        {onViewStatus && (
          <button
            type="button"
            className={styles.tile}
            onClick={() => onViewStatus('notStarted')}
          >
            <MdAddCircle
              className={styles.startNewIcon}
              color={cssVars['color-info']}
            />
            <Text
              font="body-caption"
              color="color-info"
              className={styles.tileLabel}
            >
              <FormattedMessage id="youthAdvancementsCard.startNew" />
            </Text>
          </button>
        )}
      </Flex>

      <div className={styles.recentHeading}>
        <Text strong color="color-primary" font="body-body-4">
          <FormattedMessage id="youthAdvancementsCard.recentlyUpdated" />
        </Text>
        <Text font="body-body-4" color="color-text-light">
          <FormattedMessage id="youthAdvancementsCard.last30Days" />
        </Text>
      </div>

      {recent.length ? (
        recent.map(item => (
          <AdvancementRow key={item.id} item={item} onViewMore={onViewMore} />
        ))
      ) : (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description={
            <FormattedMessage id="youthAdvancementsCard.noAdvancements" />
          }
        />
      )}

      {onViewAll && (
        <Flex justify="center" className={styles.viewAll}>
          <Button type="link" onClick={onViewAll}>
            <FormattedMessage id="youthAdvancementsCard.viewAll" />
          </Button>
        </Flex>
      )}
    </React.Fragment>
  );

  // Nothing to collapse yet, so the skeleton stands alone
  if (loading) return <Card className={styles.card} loading />;

  return (
    <Collapse
      className={styles.card}
      expandIconPosition="end"
      defaultActiveKey={defaultOpen ? ['advancements'] : []}
      items={[{ key: 'advancements', label: header, children: body }]}
    />
  );
}
